/**
 * src/lib/push/registerPush.js — Phase 1 Web Push (client).
 *
 * Subscribes this device to web push and saves the subscription server-side
 * (push.subscribe -> push_subscriptions). The service worker (public/sw.js) already
 * owns the `push`/`notificationclick` handlers, so the client only has to: ask
 * permission -> pushManager.subscribe(VAPID public) -> POST the subscription.
 *
 * VAPID public key is a build-time env var (VITE_VAPID_PUBLIC_KEY). On iOS, push
 * only works for the Home-Screen-installed PWA — pushSupported() gates the UI.
 */
import { apiFetch, API_BASE } from '../api/client';

// VAPID public key (URL-safe base64) -> Uint8Array for applicationServerKey.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

function post(action, payload) {
  return apiFetch(`${API_BASE}/data`, { method: 'POST', body: JSON.stringify({ action, ...payload }) });
}

/** True when this browser can do web push (iOS: only the installed PWA). */
export function pushSupported() {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator
    && typeof window !== 'undefined' && 'PushManager' in window && 'Notification' in window;
}

/**
 * Ask permission, subscribe, and persist the subscription.
 * @returns {Promise<{ ok: boolean, reason?: string }>}
 */
export async function enablePushNotifications() {
  if (!pushSupported()) return { ok: false, reason: 'unsupported' };
  const vapid = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  if (!vapid) return { ok: false, reason: 'no_vapid_key' };

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: 'denied' };

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid),
    });
  }

  const res = await post('push.subscribe', { subscription: sub.toJSON() });
  if (!res || res.success === false || res.error) return { ok: false, reason: res?.error || 'save_failed' };
  return { ok: true };
}

/** Fire a test push to this user's devices. @returns {Promise<{ ok, sent?, error? }>} */
export async function sendTestPush() {
  const res = await post('push.test', {});
  return { ok: !!res && res.success !== false && !res.error, ...(res || {}) };
}

const AUTO_PROMPT_KEY = 'acn_push_auto_prompted';

// Ask once per device — laptop notifications were missing simply because nobody
// had visited Settings to click "Enable" there (mobile got asked, laptop never
// did). Never re-prompts: once Notification.permission leaves 'default' the
// browser itself won't ask again, and the localStorage flag stops us retrying
// every login while it's still 'default' (user dismissed without deciding).
export async function autoEnablePushOnce() {
  if (!pushSupported()) return;
  if (typeof localStorage === 'undefined' || localStorage.getItem(AUTO_PROMPT_KEY)) return;
  if (Notification.permission !== 'default') return;
  localStorage.setItem(AUTO_PROMPT_KEY, '1');
  try { await enablePushNotifications(); } catch { /* best-effort */ }
}

/**
 * Bridge SW push events into the in-app toast layer so a focused tab shows a
 * toast, not just the OS notification. Returns an unsubscribe function.
 */
export function listenForPushToasts(onPush) {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return () => {};
  const handler = (event) => {
    if (event.data?.type === 'acn-push') onPush(event.data.payload || {});
  };
  navigator.serviceWorker.addEventListener('message', handler);
  return () => navigator.serviceWorker.removeEventListener('message', handler);
}
